import type React from "react";
import { useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
  Typography,
  Container,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/common/Textfield";
import CustomSelect from "../../../components/common/Select";
import type { IPixelPilotFormData } from "../types/pixel-form";

const PixelPilotApplicationForm = () => {
  const currentTheme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [form, setForm] = useState<IPixelPilotFormData>({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    cameraExperience: "",
    liveStreamExperience: "",
    technicalSkills: "",
    compassionScenario: "",
    reliabilityScenario: "",
    qualityFocusScenario: "",
    flowerVideo: null,
    scriptReadingVideo: null,
    availability: "",
    noticePeriod: "24h",
    whyApply: "",
    additionalNotes: "",
  });

  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const handleChange =
    (field: keyof IPixelPilotFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: "" }));
        }
      };

  const handleFileChange =
    (field: "flowerVideo" | "scriptReadingVideo") =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({
          ...prev,
          [field]: e.target.files ? e.target.files[0] : null,
        }));

        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: "" }));
        }
      };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.fullName) newErrors.fullName = "Full Name is required";
    if (!form.email) newErrors.email = "Email Address is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Email is invalid";
    if (!form.phone) newErrors.phone = "Phone Number is required";
    if (!form.location) newErrors.location = "Current City/Town is required";
    if (!form.cameraExperience)
      newErrors.cameraExperience = "Camera experience is required";
    if (!form.liveStreamExperience)
      newErrors.liveStreamExperience = "Live stream experience is required";
    if (!form.compassionScenario)
      newErrors.compassionScenario = "This field is required";
    if (!form.reliabilityScenario)
      newErrors.reliabilityScenario = "This field is required";
    if (!form.qualityFocusScenario)
      newErrors.qualityFocusScenario = "This field is required";
    if (!form.flowerVideo)
      newErrors.flowerVideo = "Flower/Plant video is required";
    if (!form.scriptReadingVideo)
      newErrors.scriptReadingVideo = "Script reading video is required";
    if (!form.availability) newErrors.availability = "Availability is required";
    if (!form.whyApply) newErrors.whyApply = "This field is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validateForm()) {
      setOpenSuccessModal(true);
      // Reset form
      setForm({
        fullName: "",
        email: "",
        phone: "",
        location: "",
        cameraExperience: "",
        liveStreamExperience: "",
        technicalSkills: "",
        compassionScenario: "",
        reliabilityScenario: "",
        qualityFocusScenario: "",
        flowerVideo: null,
        scriptReadingVideo: null,
        availability: "",
        noticePeriod: "24h",
        whyApply: "",
        additionalNotes: "",
      });
      setErrors({});
    }
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
  };

  // Fixed select handlers
  const handleSelectChange =
    (field: keyof IPixelPilotFormData) => (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      // Clear error when option is selected
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };
  // Responsive styles
  const responsiveTextareaStyle = {
    width: "100%",
    maxWidth: "800",
    padding: "10px",
    borderRadius: "4px",
    borderColor: reduxTheme.primary_colors?.[500] || "#614438",
    backgroundColor: reduxTheme.primary_colors?.[900] || "#614438",
    fontSize: "1rem",
    lineHeight: 1.5,
    color: reduxTheme.neutral_colors?.[100] || "#333",
    fontFamily: currentTheme.typography.fontFamily,
  };

  return (
    <Box
      sx={{
        bgcolor: reduxTheme.neutral_colors?.[800] || "#333",
        minHeight: "100vh",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: "center", my: { xs: 2, md: 4 } }}>
          <Typography
            variant="h4"
            component="h4"
            sx={{
              fontWeight: "bold",
              color: reduxTheme.primary_colors[400],
              marginTop: "0.5px",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
            }}
          >
            Muzukuru Pixel Pilot Recruitment Application
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: "text.secondary",
              mt: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            Join Our Dedicated Live Stream Team
          </Typography>
        </Box>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 4 } }}>
          <form onSubmit={handleSubmit}>
            {/* Personal Information Section */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                  color: reduxTheme.primary_colors[400],
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                1. Personal Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    label="Full Name"
                    variant="outlined"
                    fullWidth
                    value={form.fullName}
                    onChange={handleChange("fullName")}
                    required
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    label="Phone Number"
                    variant="outlined"
                    fullWidth
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    required
                    error={!!errors.phone}
                    helperText={errors.phone}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <CustomTextField
                    label="Current City/Town"
                    variant="outlined"
                    fullWidth
                    value={form.location}
                    onChange={handleChange("location")}
                    required
                    error={!!errors.location}
                    helperText={errors.location}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Experience & Skills Section */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                  color: reduxTheme.primary_colors[400],
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                2. Experience & Technical Skills
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Do you have prior camera operation experience?{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <InputLabel id="camera-experience-label"></InputLabel>
                <CustomSelect
                  labelId="camera-experience-label"
                  id="cameraExperience"
                  value={form.cameraExperience}
                  label=""
                  onChange={(e) =>
                    handleSelectChange("cameraExperience")(
                      e.target.value as string
                    )
                  }
                  required
                  error={!!errors.cameraExperience}
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="none">No experience</MenuItem>
                  <MenuItem value="hobbyist">Hobbyist/Amateur</MenuItem>
                  <MenuItem value="some-professional">
                    Some Professional Experience
                  </MenuItem>
                  <MenuItem value="experienced-professional">
                    Experienced Professional
                  </MenuItem>
                </CustomSelect>
                {errors.cameraExperience && (
                  <Typography color="error" variant="caption">
                    {errors.cameraExperience}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Do you have any live streaming experience?{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <InputLabel id="live-stream-experience-label"></InputLabel>
                <CustomSelect
                  labelId="live-stream-experience-label"
                  id="liveStreamExperience"
                  value={form.liveStreamExperience}
                  label=""
                  onChange={(e) =>
                    handleSelectChange("liveStreamExperience")(
                      e.target.value as string
                    )
                  }
                  required
                  error={!!errors.liveStreamExperience}
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="none">No experience</MenuItem>
                  <MenuItem value="personal">
                    Personal streaming (e.g., social media)
                  </MenuItem>
                  <MenuItem value="event">
                    Event streaming (e.g., small events)
                  </MenuItem>
                  <MenuItem value="professional">
                    Professional live production
                  </MenuItem>
                </CustomSelect>
                {errors.liveStreamExperience && (
                  <Typography color="error" variant="caption">
                    {errors.liveStreamExperience}
                  </Typography>
                )}
              </FormControl>

              <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
              >
                Describe any relevant technical skills:
              </Typography>
              <TextareaAutosize
                minRows={4}
                maxRows={8}
                value={form.technicalSkills}
                onChange={handleChange("technicalSkills")}
                placeholder="e.g., Proficient in Adobe Premiere Pro, experience with OBS Studio, basic audio mixing."
                style={responsiveTextareaStyle}
              />
            </Box>

            {/* Soft Skills Assessment Section */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                  color: reduxTheme.primary_colors[400],
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                3. Qualities & Approach
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Compassion: Describe a time you had to handle a sensitive or
                  emotional situation professionally.{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <TextareaAutosize
                  minRows={4}
                  maxRows={8}
                  value={form.compassionScenario}
                  onChange={handleChange("compassionScenario")}
                  placeholder="Please describe your experience..."
                  style={responsiveTextareaStyle}
                />
                {errors.compassionScenario && (
                  <Typography color="error" variant="caption">
                    {errors.compassionScenario}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Reliability: Describe how you ensure you meet commitments and
                  deadlines.{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <TextareaAutosize
                  minRows={4}
                  maxRows={8}
                  value={form.reliabilityScenario}
                  onChange={handleChange("reliabilityScenario")}
                  placeholder="Please describe your approach..."
                  style={responsiveTextareaStyle}
                />
                {errors.reliabilityScenario && (
                  <Typography color="error" variant="caption">
                    {errors.reliabilityScenario}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Quality Focus: How do you ensure high-quality work under
                  pressure?{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <TextareaAutosize
                  minRows={4}
                  maxRows={8}
                  value={form.qualityFocusScenario}
                  onChange={handleChange("qualityFocusScenario")}
                  placeholder="Please describe your methods..."
                  style={responsiveTextareaStyle}
                />
                {errors.qualityFocusScenario && (
                  <Typography color="error" variant="caption">
                    {errors.qualityFocusScenario}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Video Uploads Section */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                  color: reduxTheme.primary_colors[400],
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                4. Video Submissions
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Video 1: Flower/Plant Stability Test{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 2 }}
                >
                  Record a 30-60 second video of a flower or plant,
                  demonstrating your ability to keep the camera steady and
                  maintain good framing.
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={
                    <span role="img" aria-label="upload">
                      📁
                    </span>
                  }
                  sx={{
                    mt: 1,
                    width: { xs: "100%", sm: "auto" },
                  }}
                  color={errors.flowerVideo ? "error" : "primary"}
                >
                  {form.flowerVideo
                    ? form.flowerVideo.name
                    : "Upload Flower/Plant Video"}
                  <VisuallyHiddenInput
                    type="file"
                    accept="video/*"
                    onChange={handleFileChange("flowerVideo")}
                    required
                  />
                </Button>
                {errors.flowerVideo && (
                  <Typography
                    color="error"
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {errors.flowerVideo}
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Video 2: Script Reading{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  Upload a video of yourself clearly reading the script below.
                  This helps us evaluate your speaking clarity and language
                  skills.
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: currentTheme.palette.grey[50],
                    fontStyle: "italic",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    &quot;In every moment, we strive for connection. Our mission at
                    Muzukuru is to bridge distances, allowing hearts to unite,
                    even when miles apart. Through the lens, we capture not just
                    images, but memories, ensuring every detail is preserved
                    with dignity and care. We are the silent observers, the
                    steady hands, bringing comfort and clarity to those who need
                    it most.&quot;
                  </Typography>
                </Paper>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={
                    <span role="img" aria-label="upload">
                      📁
                    </span>
                  }
                  sx={{
                    mt: 1,
                    width: { xs: "100%", sm: "auto" },
                  }}
                  color={errors.scriptReadingVideo ? "error" : "primary"}
                >
                  {form.scriptReadingVideo
                    ? form.scriptReadingVideo.name
                    : "Upload Script Reading Video"}
                  <VisuallyHiddenInput
                    type="file"
                    accept="video/*"
                    onChange={handleFileChange("scriptReadingVideo")}
                    required
                  />
                </Button>
                {errors.scriptReadingVideo && (
                  <Typography
                    color="error"
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {errors.scriptReadingVideo}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Availability Section */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                  color: reduxTheme.primary_colors[400],
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                5. Availability
              </Typography>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="availability-label">
                  Please indicate your general availability for assignments:
                </InputLabel>
                <Select
                  labelId="availability-label"
                  id="availability"
                  value={form.availability}
                  label="Please indicate your general availability for assignments:"
                  onChange={(e) =>
                    handleSelectChange("availability")(e.target.value)
                  }
                  required
                  error={!!errors.availability}
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="weekdays">Weekdays (Mon-Fri)</MenuItem>
                  <MenuItem value="weekends">Weekends (Sat-Sun)</MenuItem>
                  <MenuItem value="flexible">
                    Flexible (Weekdays & Weekends)
                  </MenuItem>
                  <MenuItem value="limited">
                    Limited (Specify in notes)
                  </MenuItem>
                </Select>
                {errors.availability && (
                  <Typography color="error" variant="caption">
                    {errors.availability}
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="notice-period-label">
                  How much notice do you typically need for an assignment?
                </InputLabel>
                <Select
                  labelId="notice-period-label"
                  id="noticePeriod"
                  value={form.noticePeriod}
                  label="How much notice do you typically need for an assignment?"
                  onChange={(e) =>
                    handleSelectChange("noticePeriod")(e.target.value)
                  }
                >
                  <MenuItem value="24h">24 hours</MenuItem>
                  <MenuItem value="48h">48 hours</MenuItem>
                  <MenuItem value="72h">72 hours+</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Additional Information */}
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{
                  fontFamily: "sans-serif",
                  fontWeight: "bold",
                  color: reduxTheme.primary_colors[400],
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                6. Additional Information
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, color: "text.primary", fontWeight: "bold" }}
                >
                  Why are you interested in becoming a Muzukuru Pixel Pilot?{" "}
                  <span style={{ color: currentTheme.palette.error.main }}>
                    *
                  </span>
                </Typography>
                <TextareaAutosize
                  minRows={4}
                  maxRows={8}
                  value={form.whyApply}
                  onChange={handleChange("whyApply")}
                  placeholder="Please tell us about your motivation..."
                  style={responsiveTextareaStyle}
                />
                {errors.whyApply && (
                  <Typography color="error" variant="caption">
                    {errors.whyApply}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    color: reduxTheme.neutral_colors?.[100] || "#614438",
                    fontWeight: "bold",
                  }}
                >
                  Any other notes or questions?
                </Typography>
                <TextareaAutosize
                  minRows={4}
                  maxRows={8}
                  value={form.additionalNotes}
                  onChange={handleChange("additionalNotes")}
                  placeholder="Any additional information you'd like to share..."
                  style={responsiveTextareaStyle}
                />
              </Box>
            </Box>

            <Box sx={{ textAlign: "center", pt: 3 }}>
              <Button
                type="submit"
                sx={{
                  color: reduxTheme.primary_colors?.[800] || "#614438",
                  minWidth: { xs: "100%", sm: "200px" },
                  py: { xs: 1.5, sm: 1 },
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${reduxTheme.primary_colors?.[400] || "#C29363"} 100%)`,
                }}
              >
                Submit Application
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Success Modal */}
      <Dialog
        open={openSuccessModal}
        onClose={handleCloseSuccessModal}
        PaperProps={{
          sx: { borderRadius: "0.75rem", p: 2, mx: { xs: 2, sm: 0 } },
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            color: currentTheme.palette.secondary.main,
            fontWeight: "bold",
          }}
        >
          Application Submitted!
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "text.primary" }}>
            Thank you for your interest in becoming a Muzukuru Pixel Pilot.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={handleCloseSuccessModal}
            variant="contained"
            sx={{ color: reduxTheme.primary_colors?.[400] }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PixelPilotApplicationForm;
